import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { StatusApi } from "@/lib/apiClient";
import { Link2, Loader2, Send, Mail, Home, Trash2, Bookmark, Eye, Plus } from "lucide-react";

export default function CampaignDialog({ open, onOpenChange, leads, onSent }) {
  const [url, setUrl] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [promos, setPromos] = useState([]); // lista immobili della campagna
  const [subject, setSubject] = useState("Proposta immobiliare — SIACASA");
  const [message, setMessage] = useState("Le proponiamo in anteprima questi immobili che riteniamo possano interessarle.");
  const [sending, setSending] = useState(false);
  const [kind, setKind] = useState("immobile");
  const [savedPromos, setSavedPromos] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);

  const buildBody = () => ({
    lead_ids: leads.map((l) => l.id),
    subject, message, type: kind,
    promos: kind === "immobile" ? promos : [],
    landing_base: window.location.origin,
  });

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const r = await StatusApi.previewCampaign(buildBody());
      setPreviewHtml(r.html);
    } catch (e) {
      toast.error("Impossibile generare l'anteprima");
    } finally {
      setPreviewing(false);
    }
  };

  const loadPromos = () => StatusApi.listPromos().then(setSavedPromos).catch(() => {});
  useEffect(() => { if (open) loadPromos(); }, [open]);

  // Default oggetto/messaggio in base al tipo di email
  useEffect(() => {
    if (kind === "presentazione") {
      setSubject("Presentazione Siacasa, Servizi Immobiliari");
      setMessage("Buongiorno, sono Giorgia di Siacasa. Come da accordi, sono ad inviarle la nostra presentazione aziendale.");
    } else {
      setSubject("Proposta immobiliare — SIACASA");
      setMessage("Le proponiamo in anteprima questi immobili che riteniamo possano interessarle.");
    }
  }, [kind]);

  const sendTest = async () => {
    if (kind === "immobile" && promos.length === 0) { toast.error("Aggiungi almeno un immobile prima della prova"); return; }
    if (!testEmail.trim() || !testEmail.includes("@")) { toast.error("Inserisci un indirizzo email per la prova"); return; }
    setTestSending(true);
    try {
      const r = await StatusApi.testSendCampaign({ ...buildBody(), test_email: testEmail.trim() });
      toast.success(`Email di prova inviata a ${r.to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore nell'invio della prova";
      if ((msg || "").toLowerCase().includes("domain") || (msg || "").toLowerCase().includes("verif"))
        toast.warning("Prova non inviata: verifica prima il dominio siacasaimmobiliare.it su Resend (in sandbox l'invio è consentito solo verso l'email dell'account).");
      else toast.error(msg);
    } finally {
      setTestSending(false);
    }
  };

  const addPromo = (p) => {
    setPromos((prev) => (prev.some((x) => (x.source_url && x.source_url === p.source_url) || x.title === p.title) ? prev : [...prev, p]));
  };
  const removeFromList = (idx) => setPromos((prev) => prev.filter((_, i) => i !== idx));
  const updatePromo = (idx, patch) => setPromos((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const savePromo = async (p) => {
    try {
      await StatusApi.createPromo(p);
      loadPromos();
      toast.success("Immobile salvato in archivio");
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const removeSaved = async (id) => {
    try { await StatusApi.deletePromo(id); loadPromos(); toast.success("Immobile rimosso"); }
    catch { toast.error("Errore"); }
  };

  const withEmail = leads.filter((l) => (l.email || "").includes("@"));

  const readLink = async () => {
    if (!url.trim()) return;
    setLoadingPreview(true);
    try {
      const d = await StatusApi.previewProperty(url.trim());
      d.source_url = url.trim();
      addPromo(d);
      setUrl("");
      if (d.title && promos.length === 0) setSubject(`${d.title} — SIACASA`);
      toast.success("Immobile aggiunto");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile leggere i dati dal link");
    } finally {
      setLoadingPreview(false);
    }
  };

  const send = async () => {
    if (kind === "immobile" && promos.length === 0) { toast.error("Aggiungi almeno un immobile"); return; }
    if (!withEmail.length) { toast.error("Nessuna azienda selezionata ha un'email"); return; }
    setSending(true);
    try {
      const r = await StatusApi.sendCampaign(buildBody());
      if (r.sent > 0) toast.success(`Email inviata a ${r.sent} ${r.sent === 1 ? "azienda" : "aziende"}`);

      // Email con formato errato, scartate prima dell'invio
      const invalid = r.skipped_invalid || [];
      if (invalid.length > 0) {
        const list = invalid.map((i) => `${i.company_name || "—"} (${i.email || "vuota"})`).join(", ");
        toast.warning(`${invalid.length} email in formato errato, non inviate: ${list}. Correggi l'indirizzo nella tabella.`, { duration: 12000 });
      }

      // Email rifiutate dal server durante l'invio
      const failed = r.failed || [];
      if (failed.length > 0) {
        const list = failed.map((em) => {
          const lead = leads.find((l) => (l.email || "").trim().toLowerCase() === (em || "").trim().toLowerCase());
          return lead ? `${lead.company_name || "—"} (${em})` : em;
        }).join(", ");
        toast.error(`${failed.length} email non consegnate (indirizzo rifiutato): ${list}. Verifica gli indirizzi.`, { duration: 12000 });
      }

      onSent && onSent();
      onOpenChange(false);
      setPromos([]); setUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" /> Invia promozione immobiliare
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* recipients */}
          <div className="flex items-center gap-2 flex-wrap text-sm bg-muted/50 rounded-md p-3" data-testid="campaign-recipients">
            <Mail className="h-4 w-4 text-primary" />
            <span><b>{leads.length}</b> aziende selezionate · <b>{withEmail.length}</b> con email</span>
            <Badge variant="secondary" className="ml-auto">Email singola per azienda (privata)</Badge>
          </div>
          {withEmail.length < leads.length && (
            <p className="text-xs text-amber-600">{leads.length - withEmail.length} aziende senza email non riceveranno il messaggio.</p>
          )}

          {/* tipo campagna */}
          <div className="flex gap-2" data-testid="campaign-kind">
            <button type="button" onClick={() => setKind("immobile")} data-testid="kind-immobile"
              className={`flex-1 h-9 rounded-md text-sm font-medium border transition-colors ${kind === "immobile" ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}>
              Immobili + presentazione
            </button>
            <button type="button" onClick={() => setKind("presentazione")} data-testid="kind-presentazione"
              className={`flex-1 h-9 rounded-md text-sm font-medium border transition-colors ${kind === "presentazione" ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}>
              Solo presentazione
            </button>
          </div>

          {kind === "immobile" && (<>
          {savedPromos.length > 0 && (
            <div data-testid="promo-archive">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Immobili salvati (clicca per aggiungere)</label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {savedPromos.map((p) => (
                  <div key={p.id} className="flex items-center gap-1 border rounded-full pl-3 pr-1 py-1 text-xs bg-background hover:bg-muted">
                    <button type="button" onClick={() => addPromo(p)} className="max-w-[170px] truncate" data-testid={`pick-promo-${p.id}`}>{p.title || "Senza titolo"}</button>
                    <button type="button" onClick={() => removeSaved(p.id)} className="p-1 text-muted-foreground hover:text-red-600" data-testid={`del-promo-${p.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* property link */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Aggiungi link immobile (siacasaimmobiliare.it)</label>
            <div className="flex gap-2 mt-1">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="campaign-url"
                onKeyDown={(e) => e.key === "Enter" && readLink()}
                placeholder="Incolla il link dell'annuncio e premi Aggiungi" className="h-9" />
              <Button onClick={readLink} disabled={loadingPreview || !url.trim()} data-testid="campaign-read-link" className="h-9 shrink-0">
                {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="ml-1.5">Aggiungi</span>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Puoi aggiungere più immobili: appariranno tutti nella landing.</p>
          </div>

          {/* lista immobili */}
          {promos.map((promo, idx) => (
            <div key={idx} className="border rounded-md overflow-hidden" data-testid={`campaign-promo-${idx}`}>
              <div className="flex">
                {promo.image ? (
                  <img src={promo.image} alt="" className="w-32 h-24 object-cover shrink-0" />
                ) : (
                  <div className="w-32 h-24 bg-muted flex items-center justify-center text-muted-foreground shrink-0"><Home className="h-7 w-7" /></div>
                )}
                <div className="p-2.5 space-y-1.5 flex-1 min-w-0">
                  <Input value={promo.title || ""} onChange={(e) => updatePromo(idx, { title: e.target.value })}
                    data-testid={`campaign-title-${idx}`} placeholder="Titolo immobile" className="h-8 text-sm font-semibold" />
                  <div className="flex gap-1.5">
                    <Input value={promo.price || ""} onChange={(e) => updatePromo(idx, { price: e.target.value })}
                      data-testid={`campaign-price-${idx}`} placeholder="Prezzo" className="h-8 text-sm" />
                    <Button type="button" variant="outline" size="sm" onClick={() => savePromo(promo)} title="Salva in archivio" className="h-8 shrink-0 px-2">
                      <Bookmark className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => removeFromList(idx)} title="Rimuovi" data-testid={`remove-promo-${idx}`} className="h-8 shrink-0 px-2 text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <Textarea value={promo.description || ""} onChange={(e) => updatePromo(idx, { description: e.target.value })}
                data-testid={`campaign-desc-${idx}`} placeholder="Descrizione" rows={2} className="text-sm border-0 border-t rounded-none" />
            </div>
          ))}
          </>)}

          {/* email content */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Oggetto email</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject" className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Messaggio introduttivo</label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="campaign-message" rows={3} className="mt-1" />
          </div>

          {/* email di prova */}
          <div className="border rounded-md p-3 bg-muted/40" data-testid="campaign-test-block">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" /> Invia una prova a te stesso
            </label>
            <p className="text-[11px] text-muted-foreground mt-1">Controlla come apparirà l'email prima di inviarla alle aziende.</p>
            <div className="flex gap-2 mt-2">
              <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="campaign-test-email"
                onKeyDown={(e) => e.key === "Enter" && sendTest()}
                placeholder="tua@email.it" className="h-9" />
              <Button type="button" variant="outline" onClick={sendTest} disabled={testSending || !testEmail.trim()} data-testid="campaign-test-send-btn" className="h-9 shrink-0">
                {testSending ? <Loader2 className="h-4 w-4 animate-spin md:mr-1.5" /> : <Send className="h-4 w-4 md:mr-1.5" />}
                <span className="hidden md:inline">Invia prova</span>
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button variant="outline" onClick={openPreview} disabled={previewing} data-testid="campaign-preview-btn">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
            Anteprima
          </Button>
          <Button onClick={send} disabled={sending || !withEmail.length || (kind === "immobile" && promos.length === 0)} data-testid="campaign-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Invia a {withEmail.length} aziende
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden p-0" data-testid="email-preview-dialog">
        <DialogHeader className="px-5 pt-4">
          <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-primary" /> Anteprima email</DialogTitle>
        </DialogHeader>
        <iframe title="anteprima-email" srcDoc={previewHtml || ""} className="w-full bg-white" style={{ height: "72vh", border: 0 }} />
      </DialogContent>
    </Dialog>
    </>
  );
}
